// Shared layout wrapper for all static legal/info pages.
// Provides: logo/home link, language toggle (IT/EN), back button, sticky footer.
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Globe } from "lucide-react";
import { getStoredLang, setStoredLang } from "@/utils/i18n";

export default function StaticPageLayout({ children, titleIt, titleEn }) {
  const [lang, setLang] = useState(() => (getStoredLang() || "it").startsWith("it") ? "it" : "en");
  const navigate = useNavigate();
  const isIt = lang === "it";

  const toggleLang = () => {
    const next = isIt ? "en" : "it";
    setLang(next);
    setStoredLang(next);
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-[#e2e8f0] flex flex-col">
      <header className="border-b border-[#cfb53b]/20 bg-[#0a0a0c]/90 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-4 py-3">
          <button
            onClick={() => (history.length > 1 ? navigate(-1) : navigate("/"))}
            className="flex items-center gap-2 text-[#cfb53b] hover:text-[#d4af37] font-heading text-sm"
            data-testid="back-btn"
          >
            <ArrowLeft size={16} />
            {isIt ? "Indietro" : "Back"}
          </button>
          <Link to="/" className="font-heading text-base sm:text-lg text-[#cfb53b]">Arcane Chronicles</Link>
          <button
            onClick={toggleLang}
            className="flex items-center gap-1 text-xs text-[#94a3b8] hover:text-[#cfb53b] bg-[#1a1a1d] px-2 py-1 rounded border border-[#2d3748]"
            data-testid="lang-toggle"
          >
            <Globe size={12} />{isIt ? "IT" : "EN"}
          </button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto w-full px-4 py-6 sm:py-10">
        <h1 className="font-heading text-2xl sm:text-3xl text-[#cfb53b] mb-6 border-b border-[#cfb53b]/20 pb-3">
          {isIt ? titleIt : titleEn}
        </h1>
        <div className="prose prose-invert prose-sm sm:prose-base max-w-none text-[#cbd5e1] leading-relaxed space-y-4">
          {typeof children === "function" ? children(isIt) : children}
        </div>
        <p className="mt-10 text-[10px] text-[#64748b] italic border-t border-[#2d3748] pt-4">
          {isIt
            ? "Questo documento è fornito a titolo informativo. Non sostituisce la consulenza di un legale qualificato. Contatta un avvocato per validare la tua situazione specifica."
            : "This document is provided for informational purposes. It does not substitute qualified legal advice. Contact a lawyer to validate your specific situation."}
        </p>
      </main>

      <footer className="border-t border-[#2d3748] bg-[#0a0a0c] py-4 text-center text-[10px] text-[#64748b]">
        <div className="max-w-3xl mx-auto flex flex-wrap justify-center gap-x-4 gap-y-1 px-4">
          <Link to="/privacy" className="hover:text-[#cfb53b]">Privacy</Link>
          <Link to="/terms" className="hover:text-[#cfb53b]">{isIt ? "Termini" : "Terms"}</Link>
          <Link to="/cookies" className="hover:text-[#cfb53b]">Cookie</Link>
          <Link to="/contact" className="hover:text-[#cfb53b]">Contact</Link>
          <Link to="/about" className="hover:text-[#cfb53b]">About</Link>
        </div>
        <p className="mt-2">© {new Date().getFullYear()} Arcane Chronicles</p>
      </footer>
    </div>
  );
}
